import { NextPage } from 'next';
import { useRouter } from 'next/router';
import { FC, useCallback, useEffect, useState } from 'react';
import {
  FormProvider,
  SubmitHandler,
  useForm,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import { useGetReservationFlowProduct } from 'src/hooks/api/reservation-flow/useGetReservationFlowProduct';
import {
  ReservationFlowInvalidRequestError,
  ReservationFlowSelectedPaymentMethodCreditCardHoldingNewCreditCardInfo,
  ReservationFlowSelectedPaymentMethodNewCreditCard,
} from 'src/interfaces/reservation_flow';
import { InputPaymentInfoTemplate } from 'src/templates/merchants/purchase/InputPaymentInfoTemplate';

import { injectAppContextGetServerSideProps } from 'src/pages/_app';
import { useMerchantDetail } from 'src/hooks/api/merchant';
import { useIntl } from 'react-intl';
import { SubscriptionConfirmTemplate } from 'src/templates/merchants/purchase/SubscriptionConfirmTemplate';
import { fetchCsrfToken, fetchMerchantDetail } from 'src/infrastructure/api';
import {
  isAxiosError,
  isAPIError,
  useClientSideApiClient,
} from 'src/openapi/axiosClient';
import { isReservationFlowInvalidRequestError } from 'src/state-machines/booking/functions/reservation-flow-invalid-request/is-reservation-flow-invalid-request-error';
import { RSToastContainer } from 'src/reservation-page/toast/RSToastContainer';
import { toast } from 'src/reservation-page/toast';
import { filterReservationFlowInvalidRequestDetails } from 'src/state-machines/booking/functions/reservation-flow-invalid-request/filter-reservation-flow-invalid-request-details';
import MetaTag from 'src/components/layouts/MetaTag/MetaTag';
import { purchaseEvent } from 'src/utils/gtag';
import { VerifiedRoot } from 'src/organisms/product-purchase/VerifiedRoot';
import { useGetReservationFlowMe } from 'src/hooks/api/reservation-flow/useGetReservationFlowMe';
import { useElements, useStripe } from '@stripe/react-stripe-js';
import { Stripe, StripePaymentElementChangeEvent } from '@stripe/stripe-js';
import { ReserveStripeElements } from 'src/components/stripe/ReserveStripeElements';
import {
  convertPaymentMethodForSubmission,
  isStripeSpecificError,
  openGmoPgPaymentPopupWindowForPaymentMethod,
  openGmoPgPaymentPopupWindowForPurchase,
} from 'src/utils/payment';
import { captureException } from '@sentry/nextjs';
import { buildSiteUrl } from 'src/utils/request-url';
import { useIsMulpayOrderEnabled } from 'src/hooks/api/reservation-flow/useIsMulpayOrderEnabled';
import { TokenResponse, TokenObj } from '@mul-pay/mptoken-js';
import { MulpayCardData } from 'src/interfaces/mullpayCardData';
import { waitForPostMessage } from 'src/utils/wait-for-postmessage';

export interface SubscriptionPurchaseFormValues {
  customerInfo: { [key: string]: string };
  paymentMethod:
    | ReservationFlowSelectedPaymentMethodCreditCardHoldingNewCreditCardInfo
    | ReservationFlowSelectedPaymentMethodNewCreditCard;
  cardToken?: string;
  confirmationToken?: string;
  mulpayCardData?: MulpayCardData;
}

type Props = {
  merchantPublicId: string;
  productPublicId: number;
};

export const getServerSideProps = injectAppContextGetServerSideProps<Props>(
  async (ctx) => {
    const { query, token, session, requestId } = ctx;
    const merchantPublicId = String(query.publicId);
    const merchantDetailResponse = await fetchMerchantDetail({
      session,
      token,
      requestId,
      merchantPublicId,
    });
    if (merchantDetailResponse.redirect) {
      return { redirect: merchantDetailResponse.redirect };
    }

    const { productPublicId } = ctx.params as { productPublicId: string };

    if (isNaN(Number(productPublicId))) {
      return {
        notFound: true,
      };
    }

    return {
      props: {
        merchantPublicId,
        productPublicId: Number(productPublicId),
      },
    };
  },
);

const Inner: FC<{
  merchantPublicId: string;
  productPublicId: number;
}> = ({ merchantPublicId, productPublicId }) => {
  const router = useRouter();
  const intl = useIntl();
  const stripe = useStripe();
  const elements = useElements();
  const isMulpayOrderEnabled = useIsMulpayOrderEnabled(merchantPublicId);

  const { reset, control, setValue, resetField, setError } = useFormContext();

  const [currentPage, setCurrentPage] = useState<
    'inputPaymentInfo' | 'confirm'
  >('inputPaymentInfo');

  const formValues = useWatch({ control });
  const { data: user } = useGetReservationFlowMe();
  const { data: merchantDetail } = useMerchantDetail(merchantPublicId);
  const { data: productDetail, isLoading } = useGetReservationFlowProduct({
    parameter: {
      merchant_public_id: merchantPublicId,
      product_public_id: productPublicId,
    },
  });

  useEffect(() => {
    if (!productDetail || isLoading) return;

    reset({
      customerInfo: productDetail.customer_fields.reduce((acc, current) => {
        if (current.default_value === null) {
          return acc;
        }

        return {
          ...acc,
          [`c_${current.id}`]: current.default_value,
        };
      }, {}),
    });
  }, [productDetail, reset, isLoading]);

  const genericErrorMessage = intl.formatMessage({
    defaultMessage: 'エラーが発生しました。',
    id: 'xVVw/c',
    description:
      'src/pages/[publicId]/subscriptions/[productPublicId]/purchase/index.tsx',
  });
  const getSubmitErrorMessage = useCallback(
    (error: ReservationFlowInvalidRequestError) => {
      const errorDetails = filterReservationFlowInvalidRequestDetails(
        error.details,
      );

      if (errorDetails.length === 0) {
        return genericErrorMessage;
      }

      if (errorDetails[0].invalid_items.length === 0) {
        return genericErrorMessage;
      }

      const item = errorDetails[0].invalid_items[0];

      if (
        item.type === 'invalid_payment_method' ||
        item.type === 'unrecoverable_error'
      ) {
        return item.message;
      }

      return genericErrorMessage;
    },
    [genericErrorMessage],
  );

  const client = useClientSideApiClient();

  const fetchMulpayToken = () => {
    return formValues.mulpayCardData?.type === 'new'
      ? formValues.mulpayCardData.mulpayMpToken
      : null;
  };

  const fetchStripeCode = async (csrfToken: string) => {
    const { client_secret: stripe_client_secret } =
      await client.setupPaymentMethod({
        parameter: {
          'X-Csrf-Token': csrfToken,
        },
      });

    if (!stripe_client_secret) {
      throw new Error('stripe_client_secret is not found');
    }

    const { setupIntent, error } = await stripe!.confirmSetup({
      clientSecret: stripe_client_secret,
      confirmParams: {
        confirmation_token: formValues.confirmationToken,
      },
      redirect: 'if_required',
    });
    if (error) {
      throw error;
    }

    return setupIntent.id;
  };

  //　月謝ページでの直接購入１日決済(購入時点で決済が発生しない、クレジットの登録のみ)
  const submitSubscriptionWaitFirstAnchor = async (
    formValues: SubscriptionPurchaseFormValues,
    customer_fields: { id: number; value: string }[],
  ) => {
    // Mulpayの場合、Popup Windowを開いて3DS認証を実施させる
    // PopUpの前に非同期通信があるとblockされるので、初回に開く
    const gmoPgPopupWindow =
      formValues.paymentMethod.type === 'new_credit_card' &&
      isMulpayOrderEnabled
        ? openGmoPgPaymentPopupWindowForPaymentMethod()
        : null;
    const csrfToken = await fetchCsrfToken();

    if (formValues.paymentMethod.type === 'new_credit_card') {
      const requestBody = isMulpayOrderEnabled
        ? { mulpay_token: fetchMulpayToken() }
        : { seti_code: await fetchStripeCode(csrfToken) };

      const response = await client.createPaymentMethod({
        parameter: {
          'X-Csrf-Token': csrfToken,
        },
        requestBody,
      });

      // Mulpayの場合は3DS認証がpopUp上で完了されるまで待機する
      if (
        gmoPgPopupWindow &&
        !gmoPgPopupWindow.closed &&
        isMulpayOrderEnabled &&
        response?.gmo_pg_redirect_url &&
        response?.gmo_pg_access_id
      ) {
        // 以下から、 pupup で GMO 決済が進行する
        //   1. pupup で GMO へリダイレクトし、GMO で決済処理が進む
        //   2. pupup でそのまま　 GMO からのリダイレクトを受け取る
        //   3. pupup が 親ウインドウにメッセージを送信する
        // 1-3 までの処理完了を　waitForPostMessage　で待ち、 await promise で結果のメッセージを受け取る
        gmoPgPopupWindow.location.href = response.gmo_pg_redirect_url;

        // popup が GMO-PG からのリダイレクトを受け取り、メッセージを送信するまで
        await waitForPostMessage<{ type: 'payment:gmo_pg_returned' }>({
          sourceWindow: gmoPgPopupWindow,
          expectedType: 'payment:gmo_pg_returned',
          expectedOrigin: window.location.origin,
        });

        await client.confirmPaymentMethod({
          parameter: {
            'X-Csrf-Token': csrfToken,
          },
          requestBody: {
            gmo_pg_access_id: response.gmo_pg_access_id,
          },
        });
      }
    }

    // FIXME: サブスクを作成するだけなのでpaymentMethodのcolumn自体廃止したい
    const paymentMethod = {
      type: 'stripe_payment_method',
      stripe_credit_card_token: null, // FIXME: 以下は不要だがスキーマで required のため指定している。3DS対応を終えたらスキーマからも消して綺麗にできる
      is_restore_credit_card: false,
    } as const;
    return client.purchaseReservationFlowSubscription({
      parameter: {
        merchant_public_id: merchantPublicId,
        'X-Csrf-Token': csrfToken,
      },
      requestBody: {
        product_public_id: productPublicId,
        payment_method: paymentMethod,
        customer_fields,
      },
    });
  };

  // GmoPgの場合はpopUpモダールで3DS完了まで待機する
  const handleSubscriptionWithPaymentForGmoPg = async ({
    response,
    gmoPgPopupWindow,
    isMulpayOrderEnabled,
  }: {
    response: { gmo_pg_redirect_url?: string | null };
    gmoPgPopupWindow: Window | null;
    isMulpayOrderEnabled: boolean;
  }) => {
    const redirectUrl = response.gmo_pg_redirect_url;
    if (!redirectUrl)
      throw new Error('Unexpected error: missing gmo_pg_redirect_url');

    if (!isMulpayOrderEnabled)
      throw new Error('Unexpected error: isMulpayOrderEnabled false');

    if (!gmoPgPopupWindow || gmoPgPopupWindow.closed)
      throw new Error('Failed to open popup window');

    gmoPgPopupWindow.location.href = redirectUrl;

    // popup が GMO-PG からのリダイレクトを受け取り、メッセージを送信するまで待機
    await waitForPostMessage<{ type: 'payment:gmo_pg_returned' }>({
      sourceWindow: gmoPgPopupWindow,
      expectedType: 'payment:gmo_pg_returned',
      expectedOrigin: window.location.origin,
    });
  };

  // Stripeの場合はオーソリ取得まで実施してtokenを取得する
  const handleSubscriptionWithPaymentForStripe = async ({
    response,
    paymentMethod,
    stripe,
  }: {
    response: { stripe_client_secret?: string | null };
    paymentMethod: { confirmation_token?: string | null };
    stripe: Stripe | null;
  }) => {
    const clientSecret = response.stripe_client_secret;
    if (!clientSecret)
      throw new Error('Unexpected error: missing stripe_client_secret');
    if (!stripe) throw new Error('Stripe instance not initialized');

    const { error } = await stripe.confirmPayment({
      clientSecret,
      confirmParams: {
        confirmation_token: paymentMethod.confirmation_token ?? undefined,
      },
      redirect: 'if_required',
    });

    if (error) throw error;
  };

  // 日割り決済(購入時点で決済が発生する)
  const submitSubscriptionWithPayment = async (
    formValues: SubscriptionPurchaseFormValues,
    customer_fields: { id: number; value: string }[],
  ) => {
    // GMO-PG決済かつユーザーが保存しているクレジットカードが選択されている場合にpaymentMethod作成用にMulpayCardDataを設定
    const mulpayCardData: MulpayCardData | undefined =
      formValues.paymentMethod.type === 'credit_card' &&
      isMulpayOrderEnabled &&
      user?.credit_card?.onfile_card_identifier
        ? {
            type: 'existing',
            mulpayOnfileCardIdentifier: user.credit_card.onfile_card_identifier,
          }
        : formValues.mulpayCardData;

    const paymentMethod = convertPaymentMethodForSubmission(
      formValues.paymentMethod,
      {
        stripeConfirmationTokenId: formValues.confirmationToken,
        productType: 'subscription',
        mulpayCardData,
        isMulpayOrderEnabled: isMulpayOrderEnabled,
      },
    );

    // MEMO: ユーザー操作からpopup開く前に非同期リクエストがあるとpopUpブロックがblock対象になることがある
    // そのため、paymentMethodが確定後GmoPgの場合はあらかじめpopUpを表示する
    let gmoPgPopupWindow: Window | null = null;
    if (
      (paymentMethod.type === 'prepayment_with_gmo_credit' ||
        paymentMethod.type === 'onfile_prepayment_with_gmo_credit') &&
      !!isMulpayOrderEnabled
    ) {
      gmoPgPopupWindow =
        openGmoPgPaymentPopupWindowForPurchase(productPublicId);
    }

    const csrfToken = await fetchCsrfToken();

    const response = await client.startReservationFlowSubscriptionPurchase({
      parameter: {
        merchant_public_id: merchantPublicId,
        'X-Csrf-Token': csrfToken,
      },
      requestBody: {
        product_public_id: productPublicId,
        customer_fields,
        payment_method: paymentMethod,
      },
    });

    if (
      paymentMethod.type === 'prepayment_with_gmo_credit' ||
      paymentMethod.type === 'onfile_prepayment_with_gmo_credit'
    ) {
      await handleSubscriptionWithPaymentForGmoPg({
        response: response,
        gmoPgPopupWindow: gmoPgPopupWindow,
        isMulpayOrderEnabled: !!isMulpayOrderEnabled,
      });
    } else {
      await handleSubscriptionWithPaymentForStripe({
        response: response,
        paymentMethod: paymentMethod,
        stripe: stripe,
      });
    }

    return await client.notifyConfirmedReservationFlowSubscription({
      parameter: {
        merchant_public_id: merchantPublicId,
        'X-Csrf-Token': csrfToken,
      },
      requestBody: {
        product_issuance_id: response.product_issuance_id,
        // 月謝決済の場合、必ずカードを登録するためtrueを設定しておく
        // Stripe決済の場合はこの値に関係なくクレジットカードは登録され、こちらの値は参照されない
        save_as_last_used: true,
      },
    });
  };

  const onFormSubmit: SubmitHandler<SubscriptionPurchaseFormValues> = async (
    formValues,
  ) => {
    try {
      // 注意: submitSubscriptionWaitFirstAnchor or submitSubscriptionWithPaymentでpopUpを開くため、submit実行まで非同期通信を入れてはいけない
      // Safariなどのブラウザでpopupブロックによる決済不可になります
      const customer_fields = Object.entries(formValues.customerInfo)
        .filter(([id, value]) => {
          if (typeof value === 'undefined' || value.length === 0) {
            return false;
          }

          const parsedId = parseInt(id, 10);

          return !isNaN(parsedId);
        })
        .map(([id, value]) => {
          return { id: parseInt(id, 10), value };
        });

      let submitRes = null;
      if (!user) {
        captureException('Never');
        throw new Error('Never');
      } else if (productDetail?.is_wait_first_anchor) {
        submitRes = await submitSubscriptionWaitFirstAnchor(
          formValues,
          customer_fields,
        );
      } else {
        submitRes = await submitSubscriptionWithPayment(
          formValues,
          customer_fields,
        );
      }

      purchaseEvent({
        step: 'completion',
        productPublicId: submitRes.product_public_id?.toString() ?? '',
        customerPublicId: submitRes.customer_public_id.toString(),
      });
    } catch (error) {
      let parsedError: ReservationFlowInvalidRequestError | undefined =
        undefined;
      if (isAxiosError(error) && typeof error.response !== 'undefined') {
        if (
          isAPIError(error.response.data) &&
          isReservationFlowInvalidRequestError(error.response.data)
        ) {
          parsedError = error.response.data;
        }
      }

      let message;
      if (parsedError) {
        message = getSubmitErrorMessage(parsedError);
      } else if (isStripeSpecificError(error)) {
        // Stripe から出力されるメッセージをそのまま出すのはセキュリティ上よろしくないので、こちらで定義したメッセージを出す
        message = (
          <p>
            お支払いに失敗しました。別のカードをご利用いただくか、ご利用のカード会社にお問い合わせください。
            <a
              href="https://reserve-faq.stores.jp/hc/ja/articles/47415558684441"
              target="_blank"
              rel="noreferrer noopener"
            >
              詳細はこちら
            </a>
          </p>
        );
      } else {
        message = genericErrorMessage;
      }

      toast({
        variant: 'error',
        title: intl.formatMessage({
          defaultMessage: '月謝の購入に失敗しました',
          id: 'FXEcNi',
          description:
            'src/pages/[publicId]/subscriptions/[productPublicId]/purchase/index.tsx',
        }),
        description: message,
      });
      setCurrentPage('inputPaymentInfo');
      return;
    }

    await router.push(
      buildSiteUrl(
        `/products/${productPublicId}#product-available-services`,
        'next',
      ),
    );
    toast({
      variant: 'success',
      title: intl.formatMessage({
        defaultMessage: '月謝を購入しました',
        id: 'IMgRrJ',
        description:
          'src/pages/[publicId]/subscriptions/[productPublicId]/purchase/index.tsx',
      }),
      description: productDetail?.name,
    });
  };

  // Stripe決済の場合のみ、tokenを作成するために利用している
  const onNextFromPaymentInput = useCallback(async () => {
    if (!stripe || !elements) {
      return;
    }
    const paymentMethod = formValues.paymentMethod;
    if (!!paymentMethod && paymentMethod.type === 'new_credit_card') {
      if (isMulpayOrderEnabled) {
        setCurrentPage('confirm');
      } else {
        // Stripeの場合は、token作成もこのタイミングで行なっている
        try {
          const { confirmationToken, error } =
            await stripe.createConfirmationToken({
              elements,
              params: {
                payment_method_data: {
                  billing_details: {
                    address: {
                      country: 'JP',
                    },
                  },
                },
              },
            });
          if (error) {
            throw error;
          }
          setValue('confirmationToken', confirmationToken.id);

          setCurrentPage('confirm');
        } catch (error) {
          resetField('cardToken');
          switch ((error as stripe.StripeError).code) {
            case 'card_declined':
              setError('paymentMethod.creditCardInfo', {
                type: 'manual',
                message: intl.formatMessage({
                  defaultMessage:
                    '利用不可のカード番号です。今一度ご確認ください。',
                  id: 'A9O5sE',
                  description:
                    'src/pages/[publicId]/subscriptions/[productPublicId]/purchase/index.tsx',
                }),
              });
              break;
            case 'expired_card':
              setError('paymentMethod.creditCardInfo', {
                type: 'manual',
                message: intl.formatMessage({
                  defaultMessage:
                    '有効期限切れのカード番号です。今一度ご確認ください。',
                  id: 'KhWrTR',
                  description:
                    'src/pages/[publicId]/subscriptions/[productPublicId]/purchase/index.tsx',
                }),
              });
              break;
            default:
              setError('paymentMethod.creditCardInfo', {
                type: 'manual',
                message: intl.formatMessage({
                  defaultMessage:
                    'クレジットカード決済ができませんでした。今一度クレジットカード情報を確認してください。',
                  id: 'FvFqlX',
                  description:
                    'src/pages/[publicId]/subscriptions/[productPublicId]/purchase/index.tsx',
                }),
              });
              break;
          }
        }
      }
    } else if (!!paymentMethod && paymentMethod.type === 'credit_card') {
      setCurrentPage('confirm');
    } else {
      captureException('Never');
      throw new Error('Never');
    }
  }, [
    elements,
    formValues.paymentMethod,
    intl,
    stripe,
    isMulpayOrderEnabled,
    resetField,
    setError,
    setValue,
  ]);

  const handlePaymentElementChange = async (
    event: StripePaymentElementChangeEvent,
  ) => {
    if (!stripe || !elements || !event.complete) {
      return;
    }

    const { error: submitError } = await elements.submit();

    if (!submitError) {
      setValue(
        'paymentMethod',
        {
          type: 'new_credit_card',
          // TODO: [3ds] legacy排除後はこの形で保持しなくても良い
          creditCardInfo: {},
          // 月謝は常に保存する
          isStoreCreditCard: true,
        },
        { shouldValidate: true },
      );
    } else {
      resetField('paymentMethod.creditCardInfo');
    }
  };

  const handleMulpayTokenChange = useCallback(
    (tokenResponse?: TokenResponse) => {
      if (tokenResponse?.result != 'success') {
        setValue('mulpayCardData', undefined);
        return;
      }

      // TokenResponse　は　TokenObj　と　ErrorResponse　の　union　だが、discriminated union　になっておらず
      // resultの値からは型が推論されないので、　as　で強制型変換を行っている
      const tokenObj = tokenResponse as TokenObj;

      setValue(
        'paymentMethod',
        {
          type: 'new_credit_card',
          creditCardInfo: {},
          // 月謝は常に保存する
          isStoreCreditCard: true,
        },
        { shouldValidate: true },
      );
      setValue('mulpayCardData', {
        type: 'new',
        mulpayMpToken: tokenObj.tokenList[0],
      });
    },
    [setValue],
  );

  if (!merchantDetail) return null;

  return (
    <>
      <MetaTag
        title={intl.formatMessage({
          defaultMessage: '月謝購入',
          id: 'Y2o9C5',
          description:
            'src/pages/[publicId]/subscriptions/[productPublicId]/purchase/index.tsx',
        })}
        noIndex={true}
        googleAnalyticsFourTrackingId={
          merchantDetail.available_google_analytics_four_tracking_id
        }
        faviconImageUrl={merchantDetail?.favicon_image?.url}
        currentPath={buildSiteUrl(`/subscriptions/${productPublicId}/purchase`)}
      />
      <RSToastContainer />
      <VerifiedRoot
        merchantPublicId={merchantPublicId}
        productPublicId={productPublicId}
        pageTitle={intl.formatMessage({
          defaultMessage: '月謝購入',
          id: 'Y2o9C5',
          description:
            'src/pages/[publicId]/subscriptions/[productPublicId]/purchase/index.tsx',
        })}
      >
        {productDetail && (
          <>
            {currentPage === 'inputPaymentInfo' ? (
              <InputPaymentInfoTemplate
                onNext={onNextFromPaymentInput}
                onBack={() => router.back()}
                productDetail={productDetail}
                handlePaymentElementChange={handlePaymentElementChange}
                isMulpayOrderEnabled={!!isMulpayOrderEnabled}
                handleMulpayTokenChange={handleMulpayTokenChange}
              />
            ) : (
              <SubscriptionConfirmTemplate
                onBack={() => setCurrentPage('inputPaymentInfo')}
                onNext={onFormSubmit}
                productDetail={productDetail}
              />
            )}
          </>
        )}
      </VerifiedRoot>
    </>
  );
};

const SubscriptionPurchase: NextPage<Props> = ({
  merchantPublicId,
  productPublicId,
}) => {
  const { data: merchantDetail } = useMerchantDetail(merchantPublicId);
  const { data: productDetail } = useGetReservationFlowProduct({
    parameter: {
      merchant_public_id: merchantPublicId,
      product_public_id: productPublicId,
    },
  });

  const formMethods = useForm<SubscriptionPurchaseFormValues>({
    mode: 'onBlur',
  });

  if (!merchantDetail) return null;

  const stripeProps = productDetail?.is_wait_first_anchor
    ? ({
        mode: 'setup',
      } as const)
    : ({
        mode: 'payment',
        // 月謝は常に保存する
        savePaymentMethod: true,
        amount: productDetail?.price,
        sellerId:
          productDetail && !productDetail.is_wait_first_anchor
            ? merchantDetail.seller_external_id
            : undefined,
      } as const);

  return (
    <FormProvider {...formMethods}>
      <ReserveStripeElements {...stripeProps}>
        <Inner
          merchantPublicId={merchantPublicId}
          productPublicId={productPublicId}
        />
      </ReserveStripeElements>
    </FormProvider>
  );
};

export default SubscriptionPurchase;
